import { useEffect, useRef, useState } from "react";
import {
  comparisonStore,
} from "@/features/widgets/components/Comparison/utils/comparisonStore.tsx";
import { useTranslation } from "react-i18next";
import Dialog from "@/features/dialog/components/Dialog/Dialog.tsx";
import useAuthorizedUrl from "@/features/widgets/hooks/useAuthorizedUrl.tsx";
import useFetch from "@/features/widgets/hooks/useFetch.tsx";
import useConfig from "@/features/config/hooks/useConfig.tsx";
import useTrackEvent from "@/features/widgets/hooks/useTrackEvent.tsx";
import { ComparisonTable, CompareResult } from "@/features/widgets/components/Comparison/ComparisonTable.tsx";
import { mergeProductData } from "@/features/widgets/components/Comparison/utils/productHelper.tsx";
import { ComparisonSidePanel } from "@/features/widgets/components/Comparison/ComparisonSidePanel.tsx";
import { ComparisonConclusion } from "@/features/widgets/components/Comparison/ComparisonConclusion.tsx";
import { ComparisonAdvice, AdviceQuestionsResult } from "@/features/widgets/components/Comparison/ComparisonAdvice.tsx";
import { AdviceAttributesResult, Product, ResolvedProduct } from "@/features/widgets/components/Comparison/types/ComparisonTypes";
import "./styles/Comparison.scss";

const CLASS_PREFIX = `ws-widgets-comparison`;

const Comparison = () => {
  const { t } = useTranslation();
  const config = useConfig();
  const [products, setProducts] = useState<Product[]>([]);
  const [sidePanelOpened, setSidePanelOpened] = useState<boolean>(false);
  const [sidePanelPeeks, setSidePanelPeeks] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [fetchTriggered, setFetchTriggered] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const cssBackendHost = config.backendHost.slice(0, -2);
  const cssPath = `${ cssBackendHost }/sites/default/files/domains/${ config.domainId }/styling.css`;

  const trackEvent = useTrackEvent();

  // Whether a loading cycle has been seen for the current click. Reset
  // synchronously in the click handler, not an effect, so it's already
  // false before the dialog-open effect runs for the first time.
  const freshLoadSeen = useRef(false);

  const comparisonUrl = useAuthorizedUrl(`/api/v1/comparisons`);
  products.forEach(product => comparisonUrl.searchParams.append(`urls[]`, product.url.toString()));
  const comparisonUrlString = comparisonUrl.toString();
  const { data: comparisonData, loading: comparisonLoading, errors: comparisonErrors, warnings: comparisonWarnings, dataUrl: comparisonDataUrl } = useFetch<CompareResult>(comparisonUrlString, (fetchTriggered || dialogOpen) && products.length > 1);

  useEffect(() => {
    const unsubscribe = comparisonStore.subscribe(products => {
      setError(null);
      setProducts(products);
    });
    return unsubscribe;
  }, [config.language]);

  useEffect(() => {
    if (products.length < 2) {
      setDialogOpen(false);
      setFetchTriggered(false);
    }
    if (products.length < 1) {
      setSidePanelOpened(false);
    }
  }, [products]);

  useEffect(() => {
    if (products.length < 2) {
      return;
    }
    setSidePanelPeeks(true);
    const timeout = setTimeout(() => setSidePanelPeeks(false), 350);
    return () => clearTimeout(timeout);
  }, [products]);

  // Open the dialog once a fresh fetch for the current product selection
  // has completed (comparisonDataUrl matches) and multiple_categories is
  // clear.
  useEffect(() => {
    if (comparisonLoading) {
      freshLoadSeen.current = true;
      return;
    }
    if (!fetchTriggered || !freshLoadSeen.current) {
      return;
    }
    if (comparisonDataUrl !== comparisonUrlString) {
      return;
    }

    freshLoadSeen.current = false;
    setFetchTriggered(false);

    if (comparisonData?.status.multiple_categories) {
      setError(t(`comparison.multiple_categories`));
      return;
    }

    setDialogOpen(true);
    setSidePanelOpened(false);
  }, [fetchTriggered, comparisonLoading, comparisonData, comparisonDataUrl, comparisonUrlString]);

  const startComparison = () => {
    if (products.length < 2) {
      setError(t(`comparison.incomplete`));
      return;
    }

    const limit = comparisonData?.config.features?.compare_advanced ? 4 : 3;
    if (products.length > limit) {
      setError(t(`comparison.over_limit`, { count: limit }));
      return;
    }

    trackEvent(
      'open',
      'comparison_dialog',
      null,
      products,
    );

    freshLoadSeen.current = false;
    setFetchTriggered(true);
  }

  return (
    <>
      <link rel="stylesheet" type="text/css" href={ cssPath }/>

      <ComparisonSidePanel
        products={ products }
        sidePanelOpened={ sidePanelOpened }
        setSidePanelOpened={ setSidePanelOpened }
        sidePanelPeeks={ sidePanelPeeks }
        error={ error }
        fetchTriggered={ fetchTriggered }
        comparisonLoading={ comparisonLoading }
        onCompare={ startComparison }
      />

      <ComparisonDialog
        open={ dialogOpen }
        products={ products }
        comparisonData={ comparisonData }
        comparisonLoading={ comparisonLoading }
        comparisonErrors={ comparisonErrors }
        comparisonWarnings={ comparisonWarnings }
        onClose={
          () => {
            setDialogOpen(false);
            trackEvent(
              'close',
              `comparison_dialog`,
              null,
              products,
            );
          }
        }
      />
    </>
  )
}

type ComparisonDialogProps = {
  products: Product[];
  open: boolean;
  onClose: () => void;
  comparisonData: CompareResult | null;
  comparisonLoading: boolean;
  comparisonErrors: string[];
  comparisonWarnings: string[];
}

const ComparisonDialog = ({ products, open, onClose, comparisonData, comparisonLoading, comparisonErrors, comparisonWarnings }: ComparisonDialogProps) => {
  const { t } = useTranslation();
  const [bestProductId, setBestProductId] = useState<number | null>(null);
  const [adviceOpen, setAdviceOpen] = useState<boolean>(false);

  const advisorEnabled = comparisonData?.config.features.product_advisor || false;
  const canFetchAdvice = open && !comparisonData?.status.missing_products && products.length > 1;

  const attributesUrl = useAuthorizedUrl(`/api/v1/advices/attributes`);
  products.forEach(product => attributesUrl.searchParams.append(`urls[]`, product.url.toString()));
  const { data: attributeAdvice, loading: attributeAdviceLoading } = useFetch<AdviceAttributesResult>(
    attributesUrl.toString(),
    canFetchAdvice,
  );

  const questionsUrl = useAuthorizedUrl(`/api/v1/advices/questions`);
  products.forEach(product => questionsUrl.searchParams.append(`urls[]`, product.url.toString()));
  const { data: questionsAdvice, loading: questionsLoading } = useFetch<AdviceQuestionsResult>(
    questionsUrl.toString(),
    advisorEnabled && canFetchAdvice,
  );

  useEffect(() => {
    if (!open) {
      setAdviceOpen(false);
    }
  }, [open]);

  return (
    <Dialog
      className={ `${ CLASS_PREFIX }-dialog` }
      title={ t(`comparison.title`) }
      open={ open }
      onClose={ onClose }>
      <>
        <ComparisonTable
          products={ products }
          data={ comparisonData }
          loading={ comparisonLoading }
          errors={ comparisonErrors }
          warnings={ comparisonWarnings }
          attributeAdvice={ attributeAdvice }
          attributeAdviceLoading={ attributeAdviceLoading }
          advisorEnabled={ advisorEnabled }
          bestProductId={ bestProductId }
          onOpenAdvice={ () => setAdviceOpen(true) }
        />

        <ComparisonConclusion
          text={ attributeAdvice?.conclusion || null }
          loading={ attributeAdviceLoading }
          onOpenAdvice={ advisorEnabled ? () => setAdviceOpen(true) : undefined }
        />

        { advisorEnabled &&
          <ComparisonAdvice
            questions={ questionsAdvice?.questions }
            products={ products.map(product => mergeProductData(product, comparisonData)) as ResolvedProduct[] }
            loading={ questionsLoading }
            onComplete={ setBestProductId }
            open={ adviceOpen }
            onClose={ () => setAdviceOpen(false) }
          />
        }
      </>
    </Dialog>
  );
}

export default Comparison;
